﻿import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, Clock } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { attendanceService } from '@/services/attendance.service';

const todayISO = new Date().toISOString().split('T')[0]!;

interface AttendanceWidgetProps {
  isAdminTier: boolean;
  isManagerTier: boolean;
}

function formatTime(isoStr: string | null | undefined): string {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function statusVariant(status: string): 'success' | 'warning' | 'destructive' | 'secondary' {
  if (status === 'present') return 'success';
  if (status === 'late') return 'warning';
  if (status === 'half_day') return 'default' as 'secondary';
  if (status === 'absent') return 'destructive';
  return 'secondary';
}

function SummaryChip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className={`rounded-lg p-2.5 text-center ${color}`}>
      <p className="text-xl font-bold">{value}</p>
      <p className="mt-0.5 text-xs font-medium opacity-80">{label}</p>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded-lg bg-muted/50" />
      ))}
    </div>
  );
}

function AdminAttendance() {
  const { has } = usePermission();
  const canViewAll = has(PERMISSIONS.ATTENDANCE_VIEW_ALL);
  const canViewTeam = has(PERMISSIONS.ATTENDANCE_VIEW_TEAM);
  const enabled = canViewAll || canViewTeam;

  const summaryQ = useQuery({
    queryKey: ['dashboard', 'attendance', 'summary', todayISO],
    queryFn: () => attendanceService.getDailySummary(todayISO),
    enabled: canViewAll,
  });

  const listQ = useQuery({
    queryKey: ['dashboard', 'attendance', 'list', todayISO],
    queryFn: () => attendanceService.listDaily({ date_from: todayISO, date_to: todayISO, page: 1, limit: 10 }),
    enabled,
  });

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<Clock />}
        title="Attendance Today"
        action={
          <Link to="/hrms/attendance" className="text-xs font-normal text-primary hover:underline">
            View All
          </Link>
        }
      />
      <CardContent className="p-4">
        {summaryQ.isLoading ? (
          <div className="mb-4 grid grid-cols-4 gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted/50" />
            ))}
          </div>
        ) : summaryQ.data ? (
          <div className="mb-4 grid grid-cols-4 gap-2">
            <SummaryChip label="Present"  value={summaryQ.data.present}  color="border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-400" />
            <SummaryChip label="Late"     value={summaryQ.data.late}     color="border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-400" />
            <SummaryChip label="Half Day" value={summaryQ.data.half_day} color="border border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800/50 dark:bg-teal-950/40 dark:text-teal-400" />
            <SummaryChip label="Absent"   value={summaryQ.data.absent}   color="border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-400" />
          </div>
        ) : null}

        {listQ.isLoading ? (
          <SkeletonRows />
        ) : listQ.isError ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-muted-foreground">Failed to load records</p>
            <Button size="sm" variant="outline" onClick={() => listQ.refetch()}>Retry</Button>
          </div>
        ) : (listQ.data?.data ?? []).length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No attendance records for today</p>
        ) : (
          <div className="space-y-1.5">
            {(listQ.data?.data ?? []).map((rec) => (
              <div
                key={rec.id}
                className="flex items-center gap-3 rounded-lg border border-border/40 bg-background/40 px-3 py-2.5"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-semibold text-teal-700 dark:bg-teal-900/50 dark:text-teal-300">
                  {(rec.first_name?.[0] ?? '?')}{(rec.last_name?.[0] ?? '')}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {rec.first_name} {rec.last_name}
                    {rec.employee_code && (
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">#{rec.employee_code}</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    In: {formatTime(rec.first_punch_in)} · Out: {formatTime(rec.last_punch_out)}
                    {rec.effective_working_minutes > 0 && ` · ${(rec.effective_working_minutes / 60).toFixed(1)}h`}
                  </p>
                </div>
                <Badge variant={statusVariant(rec.status ?? '')}>
                  {(rec.status ?? 'unknown').replace('_', ' ')}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function getWeekDates(): string[] {
  const dates: string[] = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    dates.push(d.toISOString().split('T')[0]!);
  }
  return dates;
}

function UserAttendance() {
  const { has } = usePermission();
  const weekDates = getWeekDates();

  const todayStatusQ = useQuery({
    queryKey: ['dashboard', 'attendance', 'today-status'],
    queryFn: () => attendanceService.getTodayStatus(),
    enabled: has(PERMISSIONS.ATTENDANCE_VIEW_OWN),
  });

  const weekQ = useQuery({
    queryKey: ['dashboard', 'attendance', 'week', weekDates[0], weekDates[6]],
    queryFn: () =>
      attendanceService.listMyAttendance({
        date_from: weekDates[0],
        date_to: weekDates[6],
        page: 1,
        limit: 7,
      }),
    enabled: has(PERMISSIONS.ATTENDANCE_VIEW_OWN),
  });

  const record = todayStatusQ.data?.record;

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<Clock />}
        title="My Attendance"
        action={
          <Link to="/account/attendance" className="text-xs font-normal text-primary hover:underline">
            History
          </Link>
        }
      />
      <CardContent className="space-y-4 p-4">
        {todayStatusQ.isLoading ? (
          <div className="h-20 animate-pulse rounded-xl bg-muted/50" />
        ) : record ? (
          <div className="flex items-center justify-between rounded-xl border bg-teal-50/50 p-4 dark:bg-teal-950/20">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Today</p>
              <p className="text-lg font-semibold text-teal-700 dark:text-teal-300">
                {record.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </p>
              {record.first_punch_in && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  In: {formatTime(record.first_punch_in)}
                  {record.last_punch_out ? `  Out: ${formatTime(record.last_punch_out)}` : 'Still clocked in'}
                  {record.effective_working_minutes > 0
                    ? ` ${(record.effective_working_minutes / 60).toFixed(1)}h`
                    : ''}
                </p>
              )}
            </div>
            <Clock className="h-8 w-8 text-teal-400 opacity-60 dark:text-teal-500" />
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-xl border border-dashed bg-muted/20 p-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Today</p>
              <p className="text-base font-semibold text-muted-foreground">Not punched in yet</p>
              <p className="mt-0.5 text-xs text-muted-foreground">No attendance record for today</p>
            </div>
            <Clock className="h-8 w-8 text-muted-foreground opacity-30" />
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            This Week
          </p>
          {weekQ.isLoading ? (
            <div className="space-y-1.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-8 animate-pulse rounded-lg bg-muted/50" />
              ))}
            </div>
          ) : weekQ.isError ? (
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <p className="text-xs text-muted-foreground">Failed to load weekly data</p>
              <Button size="sm" variant="outline" onClick={() => weekQ.refetch()}>Retry</Button>
            </div>
          ) : (
            <div className="space-y-1.5">
              {weekDates.map((d) => {
                const dateObj = new Date(d + 'T00:00:00');
                const isSunday = dateObj.getDay() === 0;
                const isPastDay = d < todayISO;
                const rec = (weekQ.data?.data ?? []).find((r) => r.attendance_date === d);
                const dayLabel = dateObj.toLocaleDateString('en-IN', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                });
                return (
                  <div
                    key={d}
                    className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm even:bg-muted/20"
                  >
                    <span className="min-w-[96px] shrink-0 text-muted-foreground">{dayLabel}</span>
                    {isSunday && !rec ? (
                      <span className="flex-1 text-xs italic text-muted-foreground/60">Off</span>
                    ) : rec ? (
                      <>
                        <span className="flex-1 text-xs text-muted-foreground">
                          {formatTime(rec.first_punch_in)}
                          {rec.last_punch_out ? ` – ${formatTime(rec.last_punch_out)}` : ''}
                        </span>
                        <Badge variant={statusVariant(rec.status ?? '')}>
                          {(rec.status ?? '').replace('_', ' ')}
                        </Badge>
                      </>
                    ) : isPastDay ? (
                      <Badge variant="destructive">absent</Badge>
                    ) : (
                      <span className="flex-1 text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function AttendanceWidget({ isAdminTier, isManagerTier }: AttendanceWidgetProps) {
  if (isAdminTier || isManagerTier) return <AdminAttendance />;
  return <UserAttendance />;
}
